import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Container,
    IconButton,
    CircularProgress,
    Checkbox,
    Toolbar,
    Fade,
    Menu,
    MenuItem,
    Avatar,
    useTheme,
} from '@mui/material';
import MarkdownContent from './markdownContent';
import ChartContent from './chartContent';
import FollowupQuestions from './followupQuestions';
import NodeProgress from './nodeProgress';
import './chatMessages.less';
import '../../../../globals/styles/variables.less';
import xlsFileImage from '../../../../assets/images/xls-file.png';
import pptFileImage from '../../../../assets/images/ppt.png';
import AnswerAnalysisModal from '../modals/answerAnalysisModal';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ShareIcon from '@mui/icons-material/Share';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { generatePPT, generatePPTForSelectedMsgs } from '../../../../api/chatAPI';
import { useSelection } from '../contexts/SelectionContext';
import IntermediateSteps from './IntermediateSteps';
import CaliLogo from '../../../../assets/images/cali_logo.png';
import PersonIcon from '@mui/icons-material/Person';

const Typewriter = ({
    content,
    speed = 1,
    charsPerFrame = 5,
    onComplete,
    messagesEndRef,
    isStreaming,
    isFromHistory,
}) => {
    const [displayedContent, setDisplayedContent] = useState('');
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (isFromHistory) {
            setDisplayedContent(content);
            setIndex(content.length);

            if (onComplete) {
                onComplete();
            }
            return;
        }
        setDisplayedContent('');
        setIndex(0);
    }, [content, isFromHistory]);

    useEffect(() => {
        if (isFromHistory) {
            return;
        }
        if (index < content.length) {
            const timeout = setTimeout(() => {
                const nextIndex = Math.min(index + charsPerFrame, content.length);
                setDisplayedContent(content.slice(0, nextIndex));
                setIndex(nextIndex);

                // Scroll to bottom while typing
                if (messagesEndRef?.current) {
                    // Only scroll if we are near the bottom or it's a new message
                    // For now, just simple scroll
                    // messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
                }
            }, speed);
            return () => clearTimeout(timeout);
        } else if (index >= content.length && onComplete) {
            onComplete();
        }
    }, [index, content, speed, charsPerFrame, onComplete, isStreaming, isFromHistory]);

    return <MarkdownContent content={displayedContent} />;
};

const MultiChartContent = ({ charts }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % charts.length);
    };

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev - 1 + charts.length) % charts.length);
    };

    if (!charts || charts.length === 0) return null;

    return (
        <Box>
            {charts.length > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                    <IconButton onClick={handlePrev} size="small">
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ mx: 2 }}>
                        {currentIndex + 1} / {charts.length}
                    </Typography>
                    <IconButton onClick={handleNext} size="small">
                        <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}
            <ChartContent chartData={charts[currentIndex]} />
        </Box>
    );
};

const extractFilename = (urlString) => {
    if (!urlString) return null;
    const parsedUrl = new URL(urlString);
    const pathname = parsedUrl.pathname;
    const fullFilename = pathname.split('/').pop();
    return fullFilename;
};

const Message = ({
    content,
    isUser,
    chartData,
    charts,
    followupQuestions,
    excelFileURL,
    pptFileURL,
    downloadExcelFile,
    downloadPPTFile,
    msgsCount,
    index,
    onQuestionClick,
    isClarificationQuestion,
    openAnalysis,
    transparency,
    onGeneratePPTClick,
    pptLoading,
    run_id,
    nodeProgress,
    messagesEndRef,
    isStreaming,
    isFromHistory,
    isSelectionMode,
    isSelected,
    onSelectMessage,
    intermediateStepsData,
    onToggleIntermediateSteps,
}) => {
    const ExcelfileName = extractFilename(excelFileURL);
    const PPTfileName = extractFilename(pptFileURL);
    const [isTypingComplete, setIsTypingComplete] = useState(isFromHistory || !isStreaming);
    const theme = useTheme();

    const showNodeProgress = !isUser && nodeProgress && nodeProgress.length > 0;
    const isProcessing = !isUser && (!content || content === 'Processing...');

    const handleMessageClick = (event) => {
        if (isSelectionMode && !isUser) {
            event.stopPropagation();
            onSelectMessage(run_id);
        }
    };

    const handleCheckboxClick = (event) => {
        event.stopPropagation();
        onSelectMessage(run_id);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                mb: 3,
                maxWidth: '100%',
                position: 'relative',
                cursor: isSelectionMode && !isUser ? 'pointer' : 'default',
                px: 2,
            }}
            className="individual-message-wrapper"
            onClick={handleMessageClick}
        >
            {isSelectionMode && !isUser && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 20,
                        zIndex: 10,
                    }}
                >
                    <Checkbox
                        checked={isSelected}
                        onChange={handleCheckboxClick}
                        onClick={handleCheckboxClick}
                        size="medium"
                        sx={{
                            color: theme.palette.primary.main,
                            '&.Mui-checked': {
                                color: theme.palette.primary.main,
                            },
                        }}
                    />
                </Box>
            )}

            {!isUser && (
                <Avatar
                    src={CaliLogo}
                    sx={{
                        bgcolor: 'transparent',
                        mr: 2,
                        mt: 1,
                        width: 32,
                        height: 32,
                        '& img': {
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                        },
                    }}
                />
            )}

            <Box sx={{ maxWidth: isUser ? '70%' : '90%', minWidth: isUser ? 'auto' : '60%' }}>
                {isUser ? (
                    <Paper
                        elevation={0}
                        className="individual-message-paper"
                        sx={{
                            padding: '16px 24px',
                            backgroundColor: theme.palette.grey[100],
                            color: theme.palette.text.primary,
                            borderRadius: '24px 24px 4px 24px',
                            wordWrap: 'break-word',
                        }}
                    >
                        <Typography
                            className="user-message"
                            sx={{
                                fontSize: '1rem',
                                lineHeight: 1.6,
                                overflowWrap: 'break-word',
                            }}
                        >
                            {content}
                        </Typography>
                    </Paper>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {intermediateStepsData &&
                            intermediateStepsData.steps &&
                            intermediateStepsData.steps.length > 0 && (
                                <Box sx={{ width: '100%' }}>
                                    <IntermediateSteps
                                        steps={intermediateStepsData.steps}
                                        isCollapsed={intermediateStepsData.collapsed || false}
                                        onToggle={onToggleIntermediateSteps}
                                        question={intermediateStepsData.question}
                                    />
                                </Box>
                            )}

                        {showNodeProgress && (
                            <Box sx={{ mb: 1 }}>
                                <NodeProgress nodes={nodeProgress} />
                            </Box>
                        )}

                        {(content || isProcessing) && (
                            <Paper
                                elevation={0}
                                className="individual-message-paper"
                                sx={{
                                    padding: 0,
                                    backgroundColor: 'transparent',
                                    color: 'text.primary',
                                    borderRadius: '4px 24px 24px 24px',
                                    wordWrap: 'break-word',
                                    border: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                                    transition: 'all 0.2s ease-in-out',
                                }}
                            >
                                <Box className="ai-message">
                                    {isProcessing ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: 'text.secondary',
                                            }}
                                        >
                                            <CircularProgress size={16} color="inherit" />
                                            <Typography variant="body2">Processing your request...</Typography>
                                        </Box>
                                    ) : (
                                        <Typewriter
                                            content={content}
                                            speed={1}
                                            charsPerFrame={5}
                                            onComplete={() => setIsTypingComplete(true)}
                                            messagesEndRef={messagesEndRef}
                                            isStreaming={isStreaming}
                                            isFromHistory={isFromHistory}
                                        />
                                    )}

                                    {isTypingComplete && (charts || chartData) && (
                                        <Box sx={{ mt: 3 }}>
                                            {charts && charts.length > 0 ? (
                                                <MultiChartContent charts={charts} />
                                            ) : (
                                                <ChartContent chartData={chartData} />
                                            )}
                                        </Box>
                                    )}

                                    {isTypingComplete && (
                                        <Box sx={{ mt: 2 }}>
                                            <FollowupQuestions
                                                questions={followupQuestions}
                                                onQuestionClick={onQuestionClick}
                                            />
                                        </Box>
                                    )}

                                    {isTypingComplete && isClarificationQuestion > 0 && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                gap: 2,
                                                marginTop: 3,
                                                pt: 2,
                                                borderTop: `1px solid ${theme.palette.divider}`,
                                            }}
                                        >
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                sx={{ textTransform: 'none', borderRadius: 2 }}
                                                onClick={() => downloadExcelFile(excelFileURL)}
                                                startIcon={
                                                    <img src={xlsFileImage} width={20} height={20} alt="Excel" />
                                                }
                                            >
                                                Download Excel
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                size="small"
                                                sx={{ textTransform: 'none', borderRadius: 2 }}
                                                onClick={onGeneratePPTClick}
                                                disabled={pptLoading}
                                                startIcon={
                                                    pptLoading ? (
                                                        <CircularProgress size={16} />
                                                    ) : (
                                                        <img src={pptFileImage} width={20} height={20} alt="PPT" />
                                                    )
                                                }
                                            >
                                                Download PPT
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        )}

                        {isTypingComplete && isClarificationQuestion > 0 && (
                            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                <Button
                                    variant="text"
                                    size="small"
                                    sx={{
                                        color: 'text.secondary',
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                                    }}
                                    onClick={() => openAnalysis()}
                                    endIcon={<ArrowForwardIosIcon sx={{ fontSize: '12px !important' }} />}
                                >
                                    Show Analysis
                                </Button>
                                <Box sx={{ flex: 1 }} />
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton size="small" sx={{ color: 'text.disabled' }}>
                                        <ThumbUpAltOutlinedIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" sx={{ color: 'text.disabled' }}>
                                        <ThumbDownAltOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {isUser && (
                <Avatar
                    sx={{
                        bgcolor: theme.palette.secondary.main,
                        ml: 2,
                        mt: 1,
                        width: 32,
                        height: 32,
                    }}
                >
                    <PersonIcon fontSize="small" />
                </Avatar>
            )}
        </Box>
    );
};

const SelectionToolbar = ({
    selectedCount,
    totalCount,
    onSelectAll,
    onDeselectAll,
    onDeleteSelected,
    onExportSelected,
    selectedMsgPPTGeneration,
    onCancelSelection,
    pptGenerating,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <Fade in={selectedCount > 0}>
            <Paper
                elevation={4}
                sx={{
                    position: 'fixed',
                    top: 24,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000,
                    borderRadius: '32px',
                    overflow: 'hidden',
                    bgcolor: '#1a237e',
                    color: 'white',
                }}
            >
                <Toolbar
                    variant="dense"
                    sx={{
                        gap: 2,
                        pl: 2,
                        pr: 1,
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedCount} selected
                    </Typography>

                    <Box sx={{ height: 24, width: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />

                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={selectedCount === totalCount ? onDeselectAll : onSelectAll}
                        title={selectedCount === totalCount ? 'Deselect All' : 'Select All'}
                    >
                        {selectedCount === totalCount ? (
                            <DeselectIcon fontSize="small" />
                        ) : (
                            <SelectAllIcon fontSize="small" />
                        )}
                    </IconButton>
                    <IconButton size="small" color="inherit" onClick={onExportSelected} title="Download JSON">
                        <FileDownloadIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="inherit" onClick={selectedMsgPPTGeneration} title="Generate PPT">
                        {pptGenerating ? (
                            <CircularProgress size={18} sx={{ color: 'white' }} />
                        ) : (
                            <img
                                src={pptFileImage}
                                width={20}
                                height={20}
                                style={{ filter: 'brightness(0) invert(1)' }}
                                alt="PPT"
                            />
                        )}
                    </IconButton>

                    <Box sx={{ height: 24, width: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />

                    <Button
                        color="inherit"
                        onClick={onCancelSelection}
                        size="small"
                        sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto' }}
                    >
                        Cancel
                    </Button>
                </Toolbar>
            </Paper>
        </Fade>
    );
};

function ChatMessages({
    messages = [],
    messagesEndRef,
    onQuestionClick,
    chatSessionUuid,
    nodeProgress = [],
    isStreaming,
    intermediateSteps = {},
    setIntermediateSteps,
}) {
    const [openModal, setOpenModal] = useState(false);
    const [analysis, setAnalysis] = useState('');
    const [pptLoading, setPptLoading] = useState(false);
    const [pptGenerating, setPptGenerating] = useState(false);

    const [processedMessages, setProcessedMessages] = useState([]);
    const {
        isSelectionMode,
        setIsSelectionMode,
        selectedMessages,
        setSelectedMessages,
        selectableAIMessages,
        handleSelectAll,
        handleDeselectAll,
        setSelectableAIMessages,
    } = useSelection();

    useEffect(() => {
        if (processedMessages.length) {
            const aIMessages = processedMessages.filter(
                (msg) => !msg.isUser && msg.content && msg.content.trim() !== '',
            );
            setSelectableAIMessages(aIMessages);
        }
    }, [processedMessages]);
    useEffect(() => {
        let newProcessedMessages = [...messages];

        if (messages.length === 0 && nodeProgress.length > 0) {
            newProcessedMessages = [
                {
                    content: '',
                    isUser: false,
                    nodeProgress: nodeProgress,
                    run_id: `placeholder-${Date.now()}`,
                    isFromHistory: false,
                },
            ];
        } else if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage.isUser && nodeProgress.length > 0) {
                newProcessedMessages.push({
                    content: '',
                    isUser: false,
                    nodeProgress: nodeProgress,
                    run_id: `placeholder-${Date.now()}`,
                    isFromHistory: false,
                });
            } else if (!lastMessage.isUser) {
                newProcessedMessages = messages.map((msg, idx) => {
                    if (idx === messages.length - 1 && !msg.isUser) {
                        return { ...msg, nodeProgress: nodeProgress };
                    }
                    return { ...msg, nodeProgress: msg.nodeProgress || [] };
                });
            }
        }

        setProcessedMessages(newProcessedMessages);
    }, [messages, nodeProgress]);

    const handleSelectMessage = (run_id) => {
        const newSet = new Set(selectedMessages);
        if (newSet.has(run_id)) {
            newSet.delete(run_id);
        } else {
            newSet.add(run_id);
        }
        setSelectedMessages(newSet);
    };

    const handleDeleteSelected = () => {
        setSelectedMessages(new Set());
        setIsSelectionMode(false);
    };

    const handleExportSelected = () => {
        const selectedMessagesData = processedMessages.filter((msg) => !msg.isUser && selectedMessages.has(msg.run_id));

        const dataStr = JSON.stringify(selectedMessagesData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `selected-ai-messages-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    const handlePPTForSelected = async () => {
        const selectedArray = Array.from(selectedMessages);
        try {
            setPptGenerating(true);
            const params = {
                selected_messages: selectedArray,
            };
            const response = await generatePPTForSelectedMsgs(params);
            downloadPPTFile(response.latest_pptx_file_url);
        } catch (error) {
            console.error('PPT generation Error:', error);
        } finally {
            setPptGenerating(false);
        }
    };

    const handleCancelSelection = () => {
        setIsSelectionMode(false);
        setSelectedMessages(new Set());
    };

    const enterSelectionMode = () => {
        setIsSelectionMode(true);
    };

    const handleOpenAnalysisModal = (transparency) => {
        setAnalysis(transparency);
        setOpenModal(true);
    };

    const handleCloseAnalysisModal = () => {
        setAnalysis('');
        setOpenModal(false);
    };

    const downloadExcelFile = async (fileURL) => {
        try {
            const url = fileURL;
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'download.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('downloadExcelFile: Error:', error);
        }
    };

    const downloadPPTFile = async (fileURL) => {
        try {
            const url = fileURL;
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'download.pptx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('downloadPPTFile: Error:', error);
        }
    };

    const generatePPTHandler = async (msg) => {
        try {
            setPptLoading(true);

            const params = {
                session_id: chatSessionUuid,
                recharts_json: msg.additional_kwargs?.recharts_json,
                latest_question: msg.additional_kwargs?.latest_question,
                report_content: msg.content,
            };
            const response = await generatePPT(params);
            downloadPPTFile(response.latest_pptx_file_url);
        } catch (error) {
            console.error('generatePPTHandler: Error:', error);
        } finally {
            setPptLoading(false);
        }
    };

    return (
        <Container
            maxWidth="lg"
            sx={{
                flexGrow: 1,
                overflow: 'auto',
                pt: 4,
                pb: 16, // Increased padding-bottom to prevent content being hidden behind input bar
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}
            className="chat-message-container"
        >
            <SelectionToolbar
                selectedCount={selectedMessages.size}
                totalCount={selectableAIMessages.length}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                onDeleteSelected={handleDeleteSelected}
                onExportSelected={handleExportSelected}
                selectedMsgPPTGeneration={handlePPTForSelected}
                onCancelSelection={handleCancelSelection}
                pptGenerating={pptGenerating}
            />

            {processedMessages.length === 0 && nodeProgress.length === 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        opacity: 0.7,
                    }}
                >
                    <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                        How can I help you today?
                    </Typography>
                </Box>
            )}
            {processedMessages.map((message, index) => {
                // Look up intermediate steps for this message's run_id
                const messageIntermediateSteps =
                    !message.isUser && message.run_id ? intermediateSteps[message.run_id] : null;

                const handleToggleIntermediateSteps = () => {
                    if (message.run_id) {
                        setIntermediateSteps((prev) => ({
                            ...prev,
                            [message.run_id]: {
                                ...(prev[message.run_id] || { steps: [], question: '', collapsed: false }),
                                collapsed: !(prev[message.run_id]?.collapsed || false),
                            },
                        }));
                    }
                };

                return (
                    <Message
                        key={message.run_id}
                        run_id={message.run_id}
                        content={message.content}
                        isUser={message.isUser}
                        chartData={message.chartData}
                        charts={message.charts}
                        followupQuestions={message.followupQuestions}
                        excelFileURL={message.excelFileURL}
                        pptFileURL={message.pptFileURL}
                        msgsCount={processedMessages.length}
                        index={index}
                        downloadExcelFile={downloadExcelFile}
                        downloadPPTFile={downloadPPTFile}
                        onQuestionClick={onQuestionClick}
                        isClarificationQuestion={
                            message.transparency && Object.entries(message.transparency || {}).length
                        }
                        openAnalysis={() => handleOpenAnalysisModal(message.transparency)}
                        transparency={message.transparency}
                        onGeneratePPTClick={() => generatePPTHandler(message)}
                        pptLoading={pptLoading}
                        nodeProgress={message.nodeProgress || []}
                        messagesEndRef={messagesEndRef}
                        isStreaming={isStreaming}
                        isFromHistory={message.isFromHistory || false}
                        isSelectionMode={isSelectionMode}
                        isSelected={!message.isUser && selectedMessages.has(message.run_id)}
                        onSelectMessage={handleSelectMessage}
                        intermediateStepsData={messageIntermediateSteps}
                        onToggleIntermediateSteps={handleToggleIntermediateSteps}
                    />
                );
            })}
            <div ref={messagesEndRef} />
            <AnswerAnalysisModal
                analysis={analysis}
                open={openModal}
                title="Analysis"
                text="SweetPeak Confections demonstrated fluctuating monthly sales performance..."
                handleClose={handleCloseAnalysisModal}
            />
        </Container>
    );
}

export default ChatMessages;
